import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import { acknowledgeNotification, listNotifications } from '../../lib/api'
import { useAsync } from '../../lib/useAsync'
import {
  EmptyState,
  ErrorState,
  Loading,
  Notice,
  PageHeader,
  Section,
} from '../../components/ui'
import { relativeTime } from '../../lib/format'
import { notificationPermission } from '../../lib/platform'
import { useAction, useLiveReload } from './coachHooks'
import type { NotificationQueueRow, NotificationType } from '../../types/db'

const TYPE_LABEL: Record<NotificationType, string> = {
  availability_request: 'Availability request',
  assignment_created: 'New assignment',
  event_reminder_48h: 'T-48h reminder',
  event_reminder_24h: 'T-24h reminder',
  event_updated: 'Event changed',
  event_cancelled: 'Event cancelled',
  post_event_report: 'Post-event report',
  materials_check_in: 'Materials check-in',
  qualification_approved: 'Qualification approved',
  announcement: 'Announcement',
}

/**
 * The in-app inbox is the safety net behind push. Every reminder lands here
 * once it is due, whether or not the device took the push. Opening one, or
 * marking it as seen, acknowledges it, which is what removes the coach from
 * Michel's chase list. Reminders that are not due yet are not listed: the
 * queue only shows what has actually gone out.
 *
 * Copy rule: no em dashes in anything a coach reads, plain hyphens only.
 */
export function CoachInbox() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const coachId = profile?.id ?? ''
  const { data, error, loading, reload } = useAsync(() => listNotifications(coachId), [coachId])
  useLiveReload(reload)
  const action = useAction(reload)

  if (loading && !data) return <Loading label="Loading your inbox" />
  if (error && !data) return <ErrorState message={error} onRetry={reload} />

  const rows = data ?? []
  const unread = rows.filter((r) => !r.acknowledged_at)
  const read = rows.filter((r) => r.acknowledged_at)
  const permission = notificationPermission()

  async function open(row: NotificationQueueRow) {
    if (!row.acknowledged_at) {
      const ok = await action.run(() => acknowledgeNotification(row.id))
      if (!ok) return
    }
    if (row.payload.url) navigate(row.payload.url)
  }

  function markSeen(row: NotificationQueueRow) {
    void action.run(
      () => acknowledgeNotification(row.id),
      'Marked as seen. Michel can see it reached you.',
    )
  }

  return (
    <>
      <PageHeader
        title="Notifications"
        subtitle="Everything La Folie has sent you. Reminders go out 48 hours and 24 hours before each event, on Dubai time."
      />

      {permission !== 'granted' ? (
        <div style={{ marginBottom: 'var(--sp-5)' }}>
          <Notice tone="warning" title="Push is off on this device">
            Messages still land here, but you will only see them when you open the app. Turn on
            notifications from the setup screen so reminders reach you.
          </Notice>
        </div>
      ) : null}

      {action.error ? (
        <div style={{ marginBottom: 'var(--sp-5)' }}>
          <Notice tone="warning">{action.error}</Notice>
        </div>
      ) : null}
      {action.done ? (
        <div style={{ marginBottom: 'var(--sp-5)' }}>
          <Notice tone="success">{action.done}</Notice>
        </div>
      ) : null}

      {rows.length === 0 ? (
        <EmptyState
          title="Nothing yet"
          body="Availability requests, assignments and reminders all show up here once they are sent."
        />
      ) : null}

      {unread.length > 0 ? (
        <Section title="New" count={unread.length}>
          <div className="stack stack--tight">
            {unread.map((row) => (
              <NotificationRow
                key={row.id}
                row={row}
                busy={action.busy}
                onOpen={() => void open(row)}
                onMarkSeen={() => markSeen(row)}
              />
            ))}
          </div>
        </Section>
      ) : null}

      {read.length > 0 ? (
        <Section title="Earlier" count={read.length}>
          <div className="stack stack--tight">
            {read.map((row) => (
              <NotificationRow
                key={row.id}
                row={row}
                busy={action.busy}
                onOpen={() => void open(row)}
                onMarkSeen={() => markSeen(row)}
              />
            ))}
          </div>
        </Section>
      ) : null}
    </>
  )
}

function NotificationRow({
  row,
  busy,
  onOpen,
  onMarkSeen,
}: {
  row: NotificationQueueRow
  busy: boolean
  onOpen: () => void
  onMarkSeen: () => void
}) {
  const unread = !row.acknowledged_at
  return (
    <div
      className={`rowitem${unread ? ' rowitem--unread' : ''}`}
      style={{ alignItems: 'flex-start' }}
    >
      <div className="rowitem__main">
        <p className="rowitem__title" style={{ whiteSpace: 'normal' }}>
          {row.payload.title}
        </p>
        <p className="rowitem__sub">{row.payload.body}</p>
        <p className="faint" style={{ marginTop: 4 }}>
          {TYPE_LABEL[row.type]} &middot; {relativeTime(row.sent_at ?? row.send_at)}
        </p>
        <div className="btn-group" style={{ marginTop: 'var(--sp-2)' }}>
          {row.payload.url ? (
            <button type="button" className="btn btn--sm" disabled={busy} onClick={onOpen}>
              Open
            </button>
          ) : null}
          {unread ? (
            <button
              type="button"
              className="btn btn--sm btn--secondary"
              disabled={busy}
              onClick={onMarkSeen}
            >
              Mark as seen
            </button>
          ) : null}
        </div>
      </div>
      {unread ? <span className="badge badge--warning">New</span> : null}
    </div>
  )
}
